import React, { useEffect, useImperativeHandle, useRef, forwardRef } from "react";

let THREERef;
let OrbitControlsRef;

async function loadThree() {
  if (!THREERef) {
    THREERef = await import("three");
  }
  return THREERef;
}

async function loadOrbitControls() {
  if (!OrbitControlsRef) {
    const mod = await import("three/examples/jsm/controls/OrbitControls");
    OrbitControlsRef = mod.OrbitControls;
  }
  return OrbitControlsRef;
}

const disposeAll = (items) => items.forEach((item) => item?.dispose?.());

const MIME_TYPES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
  "video/mp4",
];

const getSupportedMimeType = () => {
  if (typeof window === "undefined" || typeof MediaRecorder === "undefined") {
    return null;
  }
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || null;
};

const Preview3D = forwardRef(({ image, logo }, ref) => {
  const mountRef = useRef(null);
  const workerRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const animationRef = useRef(null);
  const rotationSpeedRef = useRef(0.005);
  const isRecordingRef = useRef(false);

  useEffect(() => {
    if (!image) return;
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    const resources = {
      controls: null,
      geometries: [],
      materials: [],
      textures: [],
    };

    if (typeof window !== "undefined" && "Worker" in window) {
      workerRef.current = new Worker(
        new URL("../workers/imageProcessor.js", import.meta.url),
        { type: "module" }
      );
    }

    let THREE;
    let scene;

    const handleResize = () => {
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      if (!renderer || !camera || !mount) return;
      const { clientWidth: width, clientHeight: height } = mount;
      renderer.setSize(width, height);
      camera.aspect = width / height || 1;
      camera.updateProjectionMatrix();
    };

    const observeResize = () => {
      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(handleResize);
        observer.observe(mount);
        resizeObserverRef.current = observer;
      } else {
        window.addEventListener('resize', handleResize);
      }
    };

    const stopObservingResize = () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };

    if (workerRef.current) {
      workerRef.current.onmessage = ({ data }) => {
        if (cancelled || !THREE || !scene) return;
        const { colorBuffer, dispBuffer, width, height, maxEdgeVal } = data;

        const imgData = new ImageData(
          new Uint8ClampedArray(colorBuffer),
          width,
          height
        );
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.putImageData(imgData, 0, 0);

        // Convert to grayscale for cylinder body
        const grayscaleData = ctx.getImageData(0, 0, width, height);
        const pixels = grayscaleData.data;
        for (let i = 0; i < pixels.length; i += 4) {
          const gray = pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114;
          pixels[i] = gray;     // R
          pixels[i + 1] = gray; // G
          pixels[i + 2] = gray; // B
        }
        ctx.putImageData(grayscaleData, 0, 0);

        const texture = new THREE.CanvasTexture(canvas);
        texture.colorSpace = THREE.SRGBColorSpace;
        resources.textures.push(texture);

        // Warm emissive texture to mimic internal LED glow
        const emissiveCanvas = document.createElement("canvas");
        emissiveCanvas.width = width;
        emissiveCanvas.height = height;
        const emissiveCtx = emissiveCanvas.getContext("2d");
        const emissiveImage = emissiveCtx.createImageData(width, height);
        const gPixels = grayscaleData.data;
        const ePixels = emissiveImage.data;
        const warmColor = { r: 255, g: 225, b: 189 };
        for (let i = 0; i < gPixels.length; i += 4) {
          const normalized = gPixels[i] / 255; // grayscale so r=g=b
          const glow = Math.pow(normalized, 1.4);
          ePixels[i] = warmColor.r * glow;
          ePixels[i + 1] = warmColor.g * glow;
          ePixels[i + 2] = warmColor.b * glow;
          ePixels[i + 3] = 255;
        }
        emissiveCtx.putImageData(emissiveImage, 0, 0);
        const emissiveTexture = new THREE.CanvasTexture(emissiveCanvas);
        emissiveTexture.colorSpace = THREE.SRGBColorSpace;
        resources.textures.push(emissiveTexture);

        const dispData = new ImageData(
          new Uint8ClampedArray(dispBuffer),
          width,
          height
        );
        const dispCanvas = document.createElement("canvas");
        dispCanvas.width = width;
        dispCanvas.height = height;
        const dctx = dispCanvas.getContext("2d");
        dctx.putImageData(dispData, 0, 0);
        const dispTexture = new THREE.CanvasTexture(dispCanvas);
        resources.textures.push(dispTexture);

        const radius = 74.2 / 2; // mm
        const heightCyl = 115.6; // mm
        const displacementScale = 2.6 - 0.8;
        const displacementBias = 0.8;
        const geometry = new THREE.CylinderGeometry(
          radius,
          radius,
          heightCyl,
          256,
          1,
          true
        );
        geometry.rotateY(Math.PI);
        resources.geometries.push(geometry);
        const sideMaterial = new THREE.MeshPhongMaterial({
          color: 0xf5f1ea,
          map: texture,
          displacementMap: dispTexture,
          displacementScale,
          displacementBias,
          side: THREE.DoubleSide,
          emissive: new THREE.Color(0xffd6a6),
          emissiveMap: emissiveTexture,
          emissiveIntensity: 1.6,
          specular: new THREE.Color(0x111111),
          shininess: 18,
        });
        resources.materials.push(sideMaterial);
        const mesh = new THREE.Mesh(geometry, sideMaterial);
        mesh.userData.autoRotate = true; // Mark for auto-rotation
        scene.add(mesh);

        // Subtle translucent inner glow shell
        const innerGlowGeometry = new THREE.CylinderGeometry(
          radius - 3,
          radius - 3,
          heightCyl - 6,
          128,
          1,
          true
        );
        innerGlowGeometry.rotateY(Math.PI);
        resources.geometries.push(innerGlowGeometry);
        const innerGlowMaterial = new THREE.MeshBasicMaterial({
          color: 0xffe0b5,
          transparent: true,
          opacity: 0.2,
          side: THREE.DoubleSide,
        });
        resources.materials.push(innerGlowMaterial);
        const innerGlowMesh = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);
        innerGlowMesh.userData.autoRotate = true;
        scene.add(innerGlowMesh);

        if (logo) {
          const logoTexture = new THREE.TextureLoader().load(logo);
          logoTexture.colorSpace = THREE.SRGBColorSpace;
          resources.textures.push(logoTexture);
          const capMaterial = new THREE.MeshPhongMaterial({
            map: logoTexture,
            side: THREE.DoubleSide,
          });
          resources.materials.push(capMaterial);
          const edgeDisplacement =
            (maxEdgeVal / 255) * displacementScale + displacementBias;
          const capRadius = radius + edgeDisplacement;
          const capGeometry = new THREE.CircleGeometry(capRadius, 256);
          resources.geometries.push(capGeometry);
          const topCap = new THREE.Mesh(capGeometry, capMaterial);
          topCap.rotation.x = Math.PI / 2;
          topCap.position.y = heightCyl / 2;
          scene.add(topCap);
          const bottomCap = new THREE.Mesh(capGeometry, capMaterial);
          bottomCap.rotation.x = -Math.PI / 2;
          bottomCap.rotation.z = Math.PI;
          bottomCap.position.y = -heightCyl / 2;
          scene.add(bottomCap);
        }
      };
    }

    async function init() {
      try {
        THREE = await loadThree();
        if (cancelled) return;
        const OrbitControls = await loadOrbitControls();
        if (cancelled) return;

        scene = new THREE.Scene();
        const width = mount.clientWidth;
        const height = mount.clientHeight;
        const camera = new THREE.PerspectiveCamera(45, width / height || 1, 0.1, 1000);
        camera.position.set(0, 0, 200);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.setPixelRatio(window.devicePixelRatio || 1);
        renderer.setSize(width, height);
        rendererRef.current = renderer;
        if (cancelled) return;
        mount.appendChild(renderer.domElement);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enablePan = true;
        controls.enableZoom = true;
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.autoRotate = false; // We'll handle rotation manually

        // Track user interaction for auto-rotation
        let userInteracting = false;
        let interactionTimeout = null;

        const startInteraction = () => {
          userInteracting = true;
          clearTimeout(interactionTimeout);
        };

        const endInteraction = () => {
          clearTimeout(interactionTimeout);
          interactionTimeout = setTimeout(() => {
            userInteracting = false;
          }, 2000); // Resume auto-rotation 2 seconds after user stops interacting
        };

        controls.addEventListener('start', startInteraction);
        controls.addEventListener('end', endInteraction);
        controls.userData = { get isUserInteracting() { return userInteracting; } };

        resources.controls = controls;

        scene.add(new THREE.AmbientLight(0xfef7ef, 0.55));
        const dirLight = new THREE.DirectionalLight(0xfdf9f5, 0.6);
        dirLight.position.set(0, 0, 1);
        scene.add(dirLight);
        const innerLight = new THREE.PointLight(0xffddb0, 2.8, 600, 2);
        innerLight.position.set(0, 0, 0);
        scene.add(innerLight);

        const img = new Image();
        img.src = image;
        img.onload = () => {
          if (cancelled) return;
          if (workerRef.current) {
            if (typeof OffscreenCanvas !== "undefined") {
              const off = new OffscreenCanvas(img.width, img.height);
              const offCtx = off.getContext("2d");
              offCtx.drawImage(img, 0, 0);
              const bitmap = off.transferToImageBitmap();
              workerRef.current.postMessage(
                { imageBitmap: bitmap, width: img.width, height: img.height },
                [bitmap]
              );
            } else {
              const canvas = document.createElement("canvas");
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0);
              const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              workerRef.current.postMessage(
                { imageData: imgData },
                [imgData.data.buffer]
              );
            }
          }
        };

        const animate = () => {
          if (cancelled) return;

          // Auto-rotate cylinder if not being interacted with
          scene.traverse((obj) => {
            if (obj.userData.autoRotate && !controls.userData.isUserInteracting) {
              obj.rotation.y += rotationSpeedRef.current; // Slow automatic rotation (adjustable)
            }
          });

          controls.update();
          renderer.render(scene, camera);
          animationRef.current = requestAnimationFrame(animate);
        };
        animate();
        observeResize();
        handleResize();
      } catch (err) {
        console.error("Failed to load three.js", err);
      }
    }

    init();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animationRef.current);
      workerRef.current?.terminate();
      workerRef.current = null;
      resources.controls?.dispose?.();
      disposeAll(resources.geometries);
      disposeAll(resources.materials);
      disposeAll(resources.textures);
      rendererRef.current?.dispose?.();
      rendererRef.current = null;
      cameraRef.current = null;
      stopObservingResize();
      while (mount.firstChild) mount.removeChild(mount.firstChild);
    };
  }, [image, logo]);

  const startVideoCapture = (duration = 7000) => {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined") {
        reject(new Error("Recording is only available in the browser."));
        return;
      }

      const renderer = rendererRef.current;
      if (!renderer) {
        reject(new Error("3D preview is still preparing. Please try again."));
        return;
      }

      const canvas = renderer.domElement;
      if (!canvas || typeof canvas.captureStream !== "function") {
        reject(new Error("Canvas capture is not supported in this browser."));
        return;
      }

      if (typeof MediaRecorder === "undefined") {
        reject(new Error("MediaRecorder is not available in this browser."));
        return;
      }

      if (isRecordingRef.current) {
        reject(new Error("A recording is already in progress."));
        return;
      }

      const stream = canvas.captureStream(30);
      const mimeType = getSupportedMimeType();
      const chunks = [];
      let recorder;

      try {
        recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
      } catch (error) {
        stream.getTracks().forEach((track) => track.stop());
        reject(error);
        return;
      }

      isRecordingRef.current = true;
      const originalSpeed = rotationSpeedRef.current;
      rotationSpeedRef.current = 0.02;

      const cleanup = () => {
        rotationSpeedRef.current = originalSpeed;
        isRecordingRef.current = false;
        stream.getTracks().forEach((track) => track.stop());
      };

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        cleanup();
        reject(event.error || new Error("Recording failed."));
      };

      recorder.onstop = () => {
        const blob = chunks.length
          ? new Blob(chunks, { type: mimeType || "video/webm" })
          : null;
        cleanup();
        if (!blob) {
          reject(new Error("No video data captured."));
          return;
        }
        resolve(blob);
      };

      recorder.start(200);
      setTimeout(() => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, duration);
    });
  };

  useImperativeHandle(ref, () => ({
    captureVideo: (options) => startVideoCapture(options?.duration ?? 7000),
    isRecording: () => isRecordingRef.current,
  }));

  return <div ref={mountRef} className="absolute inset-0" />;
});

export default Preview3D;
